import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiPlus, FiChevronLeft, FiChevronRight, FiShoppingCart, FiCheckCircle,
} from 'react-icons/fi';
import API from '../utils/api';
import ReconcileModal from '../components/common/ReconcileModal';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const TYPE_LABEL  = { MATERIAL: 'Material', OUTSOURCING: 'Outsourcing', OTHER: 'Other' };
const TYPE_BADGE  = {
  MATERIAL:    { background: '#eff6ff', color: '#2563eb' },
  OUTSOURCING: { background: '#faf5ff', color: '#7c3aed' },
  OTHER:       { background: '#f3f4f6', color: '#6b7280' },
};
const STATUS_BADGE = {
  DRAFT:     { background: '#fef3c7', color: '#d97706' },
  CONFIRMED: { background: '#dcfce7', color: '#16a34a' },
  CANCELLED: { background: '#f3f4f6', color: '#9ca3af' },
};
const PAY_BADGE = {
  UNPAID:         { background: '#fee2e2', color: '#dc2626' },
  PARTIALLY_PAID: { background: '#fef3c7', color: '#d97706' },
  PAID:           { background: '#dcfce7', color: '#16a34a' },
};
const PAY_LABEL = { UNPAID: 'Unpaid', PARTIALLY_PAID: 'Partial', PAID: 'Paid' };

export default function AdminPurchases() {
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [count,     setCount]     = useState(0);
  const [pages,     setPages]     = useState(1);
  const [page,      setPage]      = useState(1);

  const [q,             setQ]             = useState('');
  const [typeFilter,    setTypeFilter]    = useState('');
  const [statusFilter,  setStatusFilter]  = useState('');
  const [payFilter,     setPayFilter]     = useState('');
  const [fromDate,      setFromDate]      = useState('');
  const [toDate,        setToDate]        = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');   // '', 'yes', 'no'
  const [showMatch,      setShowMatch]      = useState(false); // Excel match modal

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const params = { q, page, limit: 25 };
      if (typeFilter)    params.purchase_type   = typeFilter;
      if (statusFilter)  params.status          = statusFilter;
      if (fromDate)      params.from_date       = fromDate;
      if (toDate)        params.to_date         = toDate;
      if (verifiedFilter) params.verified       = verifiedFilter;
      const { data } = await API.get('/purchases', { params });
      let rows = data.purchases || [];
      if (payFilter) rows = rows.filter(p => p.payment_status === payFilter);
      setPurchases(rows);
      setPages(data.pages || 1);
      setCount(data.count || 0);
    } catch (err) {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, [q, typeFilter, statusFilter, payFilter, fromDate, toDate, verifiedFilter, page]);

  useEffect(() => { setPage(1); }, [q, typeFilter, statusFilter, payFilter, fromDate, toDate, verifiedFilter]);
  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const selectStyle = {
    padding: '8px 10px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer',
  };

  return (
    <div style={{ padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.4rem' }}>Purchases</h2>
          <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 3 }}>{count} record{count !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="crm-btn"
            onClick={() => setShowMatch(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiCheckCircle size={14} /> Match with Excel
          </button>
          <button
            className="crm-btn crm-btn--primary"
            onClick={() => navigate('/admin/purchases/new')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiPlus size={14} /> New Purchase
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' }}
            placeholder="Search vendor, purchase no. or invoice no…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
          <option value="">All Types</option>
          <option value="MATERIAL">Material</option>
          <option value="OUTSOURCING">Outsourcing</option>
          <option value="OTHER">Other</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <select value={payFilter} onChange={e => setPayFilter(e.target.value)} style={selectStyle}>
          <option value="">All Payment</option>
          <option value="UNPAID">Unpaid</option>
          <option value="PARTIALLY_PAID">Partial</option>
          <option value="PAID">Paid</option>
        </select>
        <select value={verifiedFilter} onChange={e => setVerifiedFilter(e.target.value)} style={selectStyle} title="Excel-match verification">
          <option value="">All (verified or not)</option>
          <option value="yes">Verified only</option>
          <option value="no">Not verified</option>
        </select>
        <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} onClick={e => e.target.showPicker?.()} style={{ ...selectStyle, cursor: 'pointer' }} title="From date" />
        <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   onClick={e => e.target.showPicker?.()} style={{ ...selectStyle, cursor: 'pointer' }} title="To date" />
      </div>

      {/* Table */}
      <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : purchases.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
            <FiShoppingCart size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
            <p>No purchases found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f3f4f6', background: '#fafafa' }}>
                  {['Purchase No.', 'Vendor', 'Order', 'Invoice No.', 'Date', 'Grand Total', 'Paid', 'Outstanding', 'Payment', 'Status'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {purchases.map(p => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/admin/purchases/${p.id}`)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <td style={{ padding: '11px 14px', fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {p.purchase_number}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 600 }}>{p.vendor?.company_name || '—'}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>{p.vendor?.vendor_code}</div>
                    </td>

                    <td style={{ padding: '11px 14px', color: '#6b7280', fontSize: 13, fontFamily: 'monospace' }}>
                      {p.order?.orderNumber || '—'}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#6b7280', fontSize: 13, fontFamily: 'monospace' }}>
                      {p.vendor_invoice_number || '—'}
                    </td>
                    <td style={{ padding: '11px 14px', color: '#374151', whiteSpace: 'nowrap' }}>{fmt(p.purchase_date)}</td>
                    <td style={{ padding: '11px 14px', fontWeight: 700 }}>{money(p.grand_total)}</td>
                    <td style={{ padding: '11px 14px', color: '#16a34a', fontWeight: 600 }}>{money(p.total_allocated)}</td>
                    <td style={{ padding: '11px 14px', color: p.outstanding > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                      {money(p.outstanding)}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ ...PAY_BADGE[p.payment_status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                        {PAY_LABEL[p.payment_status] || p.payment_status}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ ...STATUS_BADGE[p.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
                        {p.status}
                      </span>
                      {p.is_verified && p.status !== 'CANCELLED' && (
                        <div
                          title={`Verified${p.verify_source ? ' · ' + p.verify_source : ''}${p.verified_at ? ' · ' + fmt(p.verified_at) : ''}`}
                          style={{ fontSize: 11, color: '#15803d', fontWeight: 600, marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 }}
                        >
                          <FiCheckCircle size={11} /> Verified
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="crm-btn" style={{ padding: '6px 10px' }}>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {showMatch && (
        <ReconcileModal
          type="purchases"
          onClose={() => setShowMatch(false)}
          onVerified={fetchPurchases}
        />
      )}
    </div>
  );
}